import React from 'react';
import { Form } from 'react-bootstrap';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import { BILLING_FIELDS, OPERATOR_FLAGS } from '../operatorWizard';
import {
  OPERATOR_TYPES, OPERATOR_TYPE_LABELS, operatorTypeOf,
} from '../../../Utils/operatorType';

/**
 * The operator's sixteen boxes, in three groups.
 *
 * ONE definition of each box, rendered by both screens that own operators:
 * the create wizard shows the groups one at a time, the edit form shows all
 * three stacked as it always has. Two copies of these controls would drift —
 * a field added to the wizard and forgotten on the form is a field nobody
 * can ever change again — so there is only one.
 *
 * Every group takes the same three props and is otherwise ignorant of which
 * screen it is on. `controlId` is what ties each label to its control, so
 * the labels are also how both screens are tested.
 *
 * Every box falls back to `''` rather than to `undefined`: a brand-new
 * operator carries none of these fields yet, and React counts a box that
 * starts undefined and is then typed into as an uncontrolled input turning
 * controlled halfway through its life.
 */

function CompanyFields({ operator, onChange, validationErrors = [] }) {
  return (
    <>
      <Form.Group className="mb-3 col-12" controlId="name">
        <Form.Label>Name *</Form.Label>
        <Form.Control
          type="text"
          value={operator.name ?? ''}
          onChange={onChange}
        />
        <FormControlFeedback errors={validationErrors} field="name" />
      </Form.Group>

      {/*
        WHAT THIS RECORD IS FOR — asked here, in "The company", and not among
        the Settings.

        It is an identity question, not a preference: it decides whether this
        record is ever offered a trip, and it changes what the rest of the
        form even means (a parking bucket has no phone number, no website and
        nobody to email a digest to). The Settings section is explicitly the
        one a user is invited to skip — "already set the way operators
        normally run" — and this must not be skipped when somebody is setting
        up `AU-AA-608 Parkolás`. It sits under the name so the choice is made
        while the name is still on screen, which is the only context that
        makes it obvious.

        The options say what they mean in full, and the help text names the
        real records, because the person doing this once a year should not
        have to ask which one they want.
      */}
      <Form.Group className="mb-3 col-12" controlId="operator_type">
        <Form.Label>What is this record for? *</Form.Label>
        <Form.Select
          value={operatorTypeOf(operator)}
          onChange={onChange}
        >
          {OPERATOR_TYPES.map((value) => (
            <option value={value} key={value}>{OPERATOR_TYPE_LABELS[value]}</option>
          ))}
        </Form.Select>
        <Form.Text className="text-muted">
          Nearly always an operator — a company or person we give work to and
          pay for it. Choose expense account only for the handful of records
          that exist so an own-fleet cost has somewhere to land
          (&quot;AU-AA-608 Parkolás&quot;, &quot;AU-AA-670 Bérlési dij&quot;,
          &quot;Agent commission&quot;). An expense account is never offered
          when a trip is assigned, but costs can still be attributed to it and
          it still appears in Month End.
        </Form.Text>
        <FormControlFeedback errors={validationErrors} field="operator_type" />
      </Form.Group>

      <Form.Group className="mb-3 col-12" controlId="settlement_currency">
        <Form.Label>Settlement Currency *</Form.Label>
        <Form.Select
          value={operator.settlement_currency ?? ''}
          onChange={onChange}
        >
          <option value="">Choose settlement currency</option>
          <option>EUR</option>
          <option>HUF</option>
        </Form.Select>
        <Form.Text className="text-muted">
          The one currency this operator is settled in. A company paid in both
          is two operators — add the second from the operators list once this
          one exists.
        </Form.Text>
        <FormControlFeedback errors={validationErrors} field="settlement_currency" />
      </Form.Group>

      <Form.Group className="mb-3 col-12" controlId="email">
        <Form.Label>Email address</Form.Label>
        <Form.Control
          type="email"
          value={operator.email ?? ''}
          onChange={onChange}
        />
        <FormControlFeedback errors={validationErrors} field="email" />
      </Form.Group>

      <Form.Group className="mb-3 col-12" controlId="phone_number">
        <Form.Label>Phone Number</Form.Label>
        <Form.Control
          type="text"
          value={operator.phone_number ?? ''}
          onChange={onChange}
        />
        <FormControlFeedback errors={validationErrors} field="phone_number" />
      </Form.Group>

      <Form.Group className="mb-3 col-12" controlId="website">
        <Form.Label>Website</Form.Label>
        <Form.Control
          type="text"
          value={operator.website ?? ''}
          onChange={onChange}
        />
        <FormControlFeedback errors={validationErrors} field="website" />
      </Form.Group>
    </>
  );
}

function BillingFields({ operator, onChange, validationErrors = [] }) {
  return (
    <>
      {BILLING_FIELDS.map(({ key, label }) => (
        <Form.Group className="mb-3 col-12" controlId={`billing.${key}`} key={key}>
          <Form.Label>{label}</Form.Label>
          <Form.Control
            type="text"
            value={operator.billing?.[key] ?? ''}
            onChange={onChange}
          />
          <FormControlFeedback errors={validationErrors} field={`billing.${key}`} />
        </Form.Group>
      ))}
    </>
  );
}

function SettingsFields({ operator, onChange }) {
  return (
    <>
      {OPERATOR_FLAGS.map(({
        key, label, options, help,
      }) => (
        <Form.Group className="mb-3 col-12" controlId={key} key={key}>
          <Form.Label>{label}</Form.Label>
          <Form.Select
            value={operator[key]}
            onChange={onChange}
          >
            {options.map(([value, text]) => (
              <option value={value} key={value}>{text}</option>
            ))}
          </Form.Select>
          {help && <Form.Text className="text-muted">{help}</Form.Text>}
        </Form.Group>
      ))}
    </>
  );
}

export { CompanyFields, BillingFields, SettingsFields };
